/**
 * The heading on the cover page of each PDF.
 *
 * These used to be hardcoded as "Blood Work Analysis Report" and "Blood Work
 * Comparison Report". Practitioner-added markers and sections mean the same
 * document can now carry an organic acids, stool or hair test, so the shipped
 * default names what the report IS rather than which specimen it came from, and
 * Report Settings lets a practitioner put their own words there.
 *
 * Not gated on customLogoAllowed. The logo is the paid branding feature; the
 * title is here so a report does not misdescribe itself, which is not something
 * to sell back to the practitioner whose client is reading it. (The LabSmarts
 * mark still sits on every page either way, so this is not white-labeling.)
 */

export const DEFAULT_ANALYSIS_REPORT_TITLE = 'Lab Analysis Report';
export const DEFAULT_COMPARISON_REPORT_TITLE = 'Lab Comparison Report';

/**
 * Sized for the cover page: the title renders as a centered h3, and much past
 * this it wraps to a second line and pushes the "Prepared for" block down.
 */
export const REPORT_TITLE_MAX_LENGTH = 48;

/** Just the fields these read, so a caller can pass a plain stored profile. */
export interface ReportTitleSource {
  analysisReportTitle?: string;
  comparisonReportTitle?: string;
}

/**
 * Blank falls back to the default rather than printing an empty heading: a
 * practitioner who clears the field is asking for the standard title back, and
 * a cover page with no title at all is never what they meant. Whitespace counts
 * as blank for the same reason.
 */
const titleOrDefault = (value: string | undefined, fallback: string): string =>
  (value || '').trim() || fallback;

export const resolveAnalysisReportTitle = (
  profile?: ReportTitleSource | null,
): string =>
  titleOrDefault(
    profile ? profile.analysisReportTitle : undefined,
    DEFAULT_ANALYSIS_REPORT_TITLE,
  );

export const resolveComparisonReportTitle = (
  profile?: ReportTitleSource | null,
): string =>
  titleOrDefault(
    profile ? profile.comparisonReportTitle : undefined,
    DEFAULT_COMPARISON_REPORT_TITLE,
  );
